import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import AreaChart from "./AreaChart.jsx";
import { methods, isRequestCancelled } from "../../../../../utils/http-common.js";
import useCancellableRequest from "../../../../../hooks/useCancellableRequest.js";
import { StatMtdChartAreaSkeleton } from "../../common/ModulePageSkeleton/ModulePageSkeleton.jsx";
import {
  renderSensitiveAutocompleteOption,
  sensitiveAutocompleteInputSx,
} from "../../common/sensitiveFields.jsx";

const DEFAULT_SERIES_IDS = ["prev_year_actuals", "prev_month_actuals", "forecast", "target", "actuals"];

const LineChart = (props) => {
  const {
    month,
    handleDisableTab,
    series = DEFAULT_SERIES_IDS,
    cancelScope = "mtd-chart",
    geoFilters = {},
  } = props;

  const { request: cancellableRequest, abort: abortCancellable } =
    useCancellableRequest(cancelScope);

  const [chartData, setChartData] = useState([]);
  const [loadingLineChart, setLoadingLineChart] = useState(true);

  const [filterOptions, setFilterOptions] = useState({ zone: [], region: [], district: [], ao: [] });
  const [selectedZone, setSelectedZone] = useState(null);
  // Chart-local legacy region dropdown (UI currently commented out). Named
  // distinctly so it cannot be confused with parent geoFilters.region.
  const [chartRegion, setChartRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedAO, setSelectedAO] = useState(null);

  const parentState = String(geoFilters?.state || "").trim();
  const parentRegion = String(geoFilters?.region || "").trim();

  // Refetch filter options whenever a geo filter changes so the AO dropdown
  // narrows to AOs serving the selected zone/region/district. The backend
  // applies the cascade — see GetFilters in actualVsForecastService.go.
  useEffect(() => {
    fetchFilterOptions();
  }, [selectedZone, chartRegion, selectedDistrict, parentState, parentRegion]);

  useEffect(() => {
    getDataLineChart();
  }, [month, selectedZone, chartRegion, selectedDistrict, selectedAO, series, parentState, parentRegion]);

  useEffect(() => {
    handleDisableTab(loadingLineChart);
  }, [loadingLineChart, handleDisableTab]);

  useEffect(() => () => {
    handleDisableTab(false);
  }, [handleDisableTab]);

  const buildGeoPayload = () => ({
    zone: selectedZone?.value ?? "",
    district: selectedDistrict?.value ?? "",
    // Parent AO-tracker filters win. Chart-local region only fills in when
    // parent did not send one (legacy chart region dropdown).
    state: parentState,
    region: parentRegion || (chartRegion?.value ?? ""),
  });

  const fetchFilterOptions = async () => {
    let fetchError;
    try {
      const payload = buildGeoPayload();
      let responseData;
      [fetchError, responseData] = await cancellableRequest(
        `salespnf/getfilters`,
        methods.POST,
        payload,
        { cancelKey: "filters" }
      );
      if (isRequestCancelled(fetchError)) return;
      if (responseData?.data) {
        const next = { zone: [], region: [], district: [], ao: [] };
        responseData.data.forEach((f) => {
          if (f.key === "zone") next.zone = f.value;
          if (f.key === "region") next.region = f.value;
          if (f.key === "district") next.district = f.value;
          if (f.key === "ao") next.ao = f.value;
        });
        setFilterOptions(next);
      }
    } catch (error) {
      if (!isRequestCancelled(error)) {
        console.error("Failed to fetch filter options", error);
      }
    }
  };

  const getDataLineChart = async () => {
    if (!month) {
      abortCancellable("chart");
      setChartData([]);
      setLoadingLineChart(false);
      return;
    }

    setLoadingLineChart(true);
    let fetchError;

    try {
      const payload = {
        month,
        series,
        account_owner: selectedAO?.value ?? "",
        ...buildGeoPayload(),
      };

      let responseData;
      [fetchError, responseData] = await cancellableRequest(
        `salespnf/getmtdchart`,
        methods.POST,
        payload,
        { cancelKey: "chart" }
      );

      if (isRequestCancelled(fetchError)) return;

      if (responseData?.data?.row_data) {
        const data = responseData.data.row_data.map((x) => {
          const dataIn = x.data.map((yData) => {
            yData.y = yData.sum;
            return yData;
          });
          return { ...x, data: dataIn };
        });
        setChartData(data);
      }
    } catch (error) {
      if (!isRequestCancelled(error)) {
        console.error("Failed to fetch line chart data", error);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setLoadingLineChart(false);
      }
    }
  };

  // Clearing narrower selections on broader change avoids invalid combos
  // (e.g. Zone=EAST + Region=GUJARAT) producing an empty chart. AO is also
  // cleared because the eligible AO list is geo-cascaded.
  const handleZoneChange = (_, value) => {
    setSelectedZone(value);
    setChartRegion(null);
    setSelectedDistrict(null);
    setSelectedAO(null);
  };

  const handleRegionChange = (_, value) => {
    setChartRegion(value);
    setSelectedDistrict(null);
    setSelectedAO(null);
  };

  const handleDistrictChange = (_, value) => {
    setSelectedDistrict(value);
    setSelectedAO(null);
  };

  const handleAOChange = (_, value) => {
    setSelectedAO(value);
  };

  const filterSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
      fontSize: 12,
    },
  };

  const isOptionEqualToValue = (option, value) => option?.value === value?.value;

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          {/* <Box sx={{ width: 220 }} className="fullWidthOnMobile">
            <Autocomplete
              size="small"
              fullWidth
              options={filterOptions.zone || []}
              value={selectedZone}
              onChange={handleZoneChange}
              isOptionEqualToValue={isOptionEqualToValue}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Zone" variant="outlined" sx={filterSx} />
              )}
            />
          </Box>
          <Box sx={{ width: 220 }} className="fullWidthOnMobile">
            <Autocomplete
              size="small"
              fullWidth
              options={filterOptions.region || []}
              value={chartRegion}
              onChange={handleRegionChange}
              isOptionEqualToValue={isOptionEqualToValue}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Region" variant="outlined" sx={filterSx} />
              )}
            />
          </Box> */}
          <Box sx={{ width: 220 }} className="fullWidthOnMobile">
            <Autocomplete
              size="small"
              fullWidth
              options={filterOptions.district || []}
              value={selectedDistrict}
              onChange={handleDistrictChange}
              isOptionEqualToValue={isOptionEqualToValue}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select District" variant="outlined" sx={filterSx} />
              )}
            />
          </Box>
          <Box sx={{ width: 220 }} className="fullWidthOnMobile">
            <Autocomplete
              size="small"
              fullWidth
              options={filterOptions.ao || []}
              value={selectedAO}
              onChange={handleAOChange}
              isOptionEqualToValue={isOptionEqualToValue}
              renderOption={renderSensitiveAutocompleteOption}
              sx={sensitiveAutocompleteInputSx(!!selectedAO)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Account Owner" variant="outlined" sx={filterSx} />
              )}
            />
          </Box>
          <Typography sx={{ fontSize: 12, color: '#666', fontStyle: 'italic', flexShrink: 0, ml: 'auto' }}>
            * MTD Target Calculated based on 3 month purchase pattern
          </Typography>
        </Box>

        <Box display="flex" flexDirection={"column"} width={"100%"} >
          {/* Chart */}
          {loadingLineChart ? (
            <StatMtdChartAreaSkeleton />
          ) : (
            <Box
              flex={1}
              sx={{ height: "100%" }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <AreaChart
                data={chartData}
                customYAxisLimits={true}
                tradeTarget={190}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LineChart;
